import React, { useMemo } from 'react';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent
} from '@material-ui/lab';
import { Paper, Typography, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { formatMoney } from '../../utils';
import moment from 'moment';

// Icons
import { CirclePlusIcon, CartIcon, SyncIcon } from '../../icons';

// Alias for backward compatibility
const AddCircleOutlineIcon = CirclePlusIcon;
const ShoppingCartIcon = CartIcon;
const SyncAltIcon = SyncIcon;

const useStyles = makeStyles(theme => ({
    paper: {
        padding: '10px 16px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: 'transparent' // Simplistic design
    },
    oppositeContent: {
        flex: 0.2,
        paddingRight: theme.spacing(2)
    },
    // No styles for timelineDot needed for a simple look
    positive: {
        color: theme.palette.success.main,
        borderColor: 'transparent'
    },
    negative: {
        color: theme.palette.error.main,
        borderColor: 'transparent'
    },
    neutral: {
        color: theme.palette.info.main,
        borderColor: 'transparent'
    },
    amountText: {
        fontWeight: 'bold'
    }
}));

const ICONS = {
    TRANSFER_IN: <SyncAltIcon />,
    TRANSFER_OUT: <SyncAltIcon />,
    PURCHASE: <ShoppingCartIcon />,
    REFUND: <AddCircleOutlineIcon />,
    CHARGE: <AddCircleOutlineIcon />
};

const getTransactionTypeStyle = (type, classes) => {
    switch (type) {
        case 'TRANSFER_IN':
        case 'CHARGE':
        case 'REFUND':
            return classes.positive;
        case 'TRANSFER_OUT':
        case 'PURCHASE':
            return classes.negative;
        default:
            return classes.neutral;
    }
};

function HistoryItem({ item }) {
    const classes = useStyles();
    const {
        type,
        date,
        title,
        amount,
        description,
        bookingItems,
        maxRefund,
        agentName,
        guestDebt
    } = item;
    const isPositive = ['TRANSFER_IN', 'CHARGE', 'REFUND'].includes(type);

    return (
        <TimelineItem>
            <TimelineOppositeContent className={classes.oppositeContent}>
                <Typography variant="body2" color="textSecondary">
                    {moment(date).format('MMM DD, YYYY')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {moment(date).format('h:mm A')}
                </Typography>
            </TimelineOppositeContent>
            <TimelineSeparator>
                <TimelineDot variant="outlined" className={getTransactionTypeStyle(type, classes)}>
                    {ICONS[type]}
                </TimelineDot>
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
                <Paper elevation={0} variant="outlined" className={classes.paper}>
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h6" component="h1">
                                {title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {description}
                            </Typography>
                            {guestDebt && (
                                <Typography
                                    variant="caption"
                                    component={'div'}
                                    color={guestDebt ? 'error' : 'textPrimary'}
                                >
                                    {`Processed by: ${agentName}`}
                                </Typography>
                            )}
                            {type === 'TRANSFER_IN' && (
                                <Typography variant="body2" color="textSecondary">
                                    Max Refund: {formatMoney(maxRefund)}
                                </Typography>
                            )}
                            {bookingItems?.map(item => (
                                <small key={item.id}>
                                    {' - '}
                                    {item.name} -{' '}
                                    {moment(item.start).format('ddd MMM DD, YYYY [at] h:mm A')} -{' '}
                                    {item.status}
                                </small>
                            ))}
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="h6"
                                component="h1"
                                className={`${classes.amountText} ${getTransactionTypeStyle(
                                    type,
                                    classes
                                )}`}
                            >
                                {isPositive ? `+${formatMoney(amount)}` : formatMoney(amount)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </TimelineContent>
        </TimelineItem>
    );
}

function BalanceHistory({ payments, paymentMethods, balanceSlug }) {
    const processedHistory = useMemo(() => {
        if (!payments || !paymentMethods) {
            return [];
        }

        const processedIds = new Set();
        let history = [];

        const sortedPayments = [...payments].sort((a, b) => new Date(b.date) - new Date(a.date));

        for (const payment of sortedPayments) {
            if (processedIds.has(payment.id)) {
                continue;
            }

            // Case 1: Balance Fill / Transfer In (e.g. Credit Card -> Balance)
            if (
                payment.bookingId === null &&
                payment.type === 'refund' &&
                payment.paymentSlug === balanceSlug
            ) {
                const pair = sortedPayments.find(
                    p =>
                        `${p.id}` === `${payment.stripe_id}` &&
                        p.paymentSlug !== balanceSlug &&
                        p.bookingId === null
                );
                if (pair) {
                    history.push({
                        id: payment.id,
                        type: 'TRANSFER_IN',
                        date: payment.date,
                        title: 'Balance Top-up',
                        amount: -1 * payment.amount,
                        maxRefund: pair.maxRefundAmount,
                        description: `From ${paymentMethods.find(pm => pm.slug === pair.paymentSlug)
                            ?.name || 'source'} with transaction amount ${formatMoney(
                            pair.amount
                        )}`,
                        agentName: payment.agentName,
                        guestDebt: payment.agent_id && payment.agent_id !== payment.booker_id
                    });
                    processedIds.add(payment.id);
                    processedIds.add(pair.id);
                    continue;
                }
            }

            // Case 2: Balance Refund / Transfer Out (e.g. Balance -> Credit Card)
            /*
            if (
                payment.bookingId === null &&
                payment.paymentSlug === balanceSlug &&
                payment.type === 'refund'
            ) {
                const pair = sortedPayments.find(
                    p => p.stripe_id === payment.id && p.type === 'refund' && p.id !== payment.id
                );
                if (pair) {
                    history.push({
                        id: payment.id,
                        type: 'TRANSFER_OUT',
                        date: payment.date,
                        title: 'Balance Refund',
                        amount: pair.amount, // should be negative
                        description: `To ${paymentMethods.find(pm => pm.slug === pair.paymentSlug)
                            ?.name || 'destination'}`
                    });
                    processedIds.add(payment.id);
                    processedIds.add(pair.id);
                    continue;
                }
            }*/

            // Case 3: Simple Buy / Purchase from balance
            if (payment.paymentSlug === balanceSlug && payment.bookingId !== null) {
                if (payment.type === 'refund') {
                    history.push({
                        id: payment.id,
                        type: 'REFUND',
                        date: payment.date,
                        title: 'Refund',
                        amount: -payment.amount,
                        description: payment.note,
                        guestDebt: false
                    });
                    continue;
                }
                history.push({
                    id: payment.id,
                    type: 'PURCHASE',
                    date: payment.date,
                    title: 'Activity Purchase',
                    bookingItems: payment.bookingItems,
                    amount: -payment.amount,
                    description:
                        payment.bookingItems?.map(item => item.name).join(', ') || 'Booking',
                    guestDebt: false
                });
                processedIds.add(payment.id);
                continue;
            }

            // Fallback for simple transactions not part of a balance transfer
            /*
            const paymentMethod = paymentMethods.find(pm => pm.slug === payment.paymentSlug);
            if (payment.paymentSlug !== balanceSlug) {
                if (payment.type === 'charge' || payment.type === 'payment') {
                    history.push({
                        id: payment.id,
                        type: 'CHARGE',
                        date: payment.date,
                        title: 'Payment',
                        amount: payment.amount,
                        description: payment.note || `Using ${paymentMethod?.name}`
                    });
                } else if (payment.type === 'refund') {
                    history.push({
                        id: payment.id,
                        type: 'REFUND',
                        date: payment.date,
                        title: 'Refund',
                        amount: payment.amount, // should be negative
                        description: payment.note || `To ${paymentMethod?.name}`
                    });
                }
                processedIds.add(payment.id);
            }*/
        }

        return history.sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [payments, paymentMethods, balanceSlug]);

    if (processedHistory.length === 0) {
        return (
            <Typography
                variant="body1"
                color="textSecondary"
                style={{ textAlign: 'center', margin: '20px' }}
            >
                No transaction history yet.
            </Typography>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            <Timeline align="left">
                {processedHistory.map(item => (
                    <HistoryItem key={item.id} item={item} />
                ))}
            </Timeline>
        </div>
    );
}

export default BalanceHistory;
